import { MapPin } from "lucide-react";
import { useLang } from "../context/LanguageContext";

/**
 * Tiles are ordered for a bento-style feel.
 * `src` points to /public/gallery. If a user-supplied file exists there the
 * browser will load it; otherwise the fallback stock URL is used (onError swap).
 */
const TILES = [
  { id: "stock1", src: "/gallery/stock-hinge-01.jpg", fallback: null, tagKey: "tagStock", span: "col-span-2 row-span-2" },
  { id: "warehouse1", src: "/gallery/warehouse-01.jpg", fallback: "https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tagKey: "tagWarehouse", span: "col-span-1 row-span-1", needsPhoto: true },
  { id: "container1", src: "/gallery/container-01.jpg", fallback: "https://images.pexels.com/photos/5025533/pexels-photo-5025533.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tagKey: "tagContainer", span: "col-span-1 row-span-1", needsPhoto: true },
  { id: "stock2", src: "/gallery/stock-hinge-02.jpg", fallback: null, tagKey: "tagStock", span: "col-span-1 row-span-1" },
  { id: "factory1", src: "/gallery/warehouse-02.jpg", fallback: "https://images.pexels.com/photos/8347501/pexels-photo-8347501.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tagKey: "tagFactory", span: "col-span-1 row-span-1", needsPhoto: true },
  { id: "stock3", src: "/gallery/stock-hinge-03.jpg", fallback: null, tagKey: "tagStock", span: "col-span-1 row-span-2" },
  { id: "container2", src: "/gallery/container-02.jpg", fallback: "https://images.pexels.com/photos/906494/pexels-photo-906494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tagKey: "tagContainer", span: "col-span-2 row-span-1", needsPhoto: true },
  { id: "stock4", src: "/gallery/stock-hinge-04.jpg", fallback: null, tagKey: "tagStock", span: "col-span-1 row-span-1" },
];

function Tile({ tile, t }) {
  const handleError = (e) => {
    if (tile.fallback && e.currentTarget.src !== tile.fallback) {
      e.currentTarget.src = tile.fallback;
    }
  };

  return (
    <div
      className={`relative overflow-hidden bg-[#0B1B29] group ${tile.span}`}
      data-testid={`gallery-tile-${tile.id}`}
    >
      <img
        src={tile.src}
        alt={t.common.gallery[tile.tagKey]}
        onError={handleError}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1B29]/70 via-[#0B1B29]/20 to-transparent" />
      {tile.needsPhoto && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="bg-[#0B1B29]/80 text-white border border-white/20 text-[10px] font-mono tracking-[0.22em] uppercase px-3 py-2">
            {t.common.gallery.placeholder}
          </span>
        </div>
      )}
      <div className="absolute bottom-3 left-3 text-white">
        <span className="inline-flex items-center gap-1.5 bg-[#0B1B29]/85 border border-white/15 text-[10px] font-mono tracking-[0.18em] uppercase px-2.5 py-1">
          <MapPin size={11} className="text-[#F59E0B]" />
          {t.common.gallery[tile.tagKey]}
        </span>
      </div>
    </div>
  );
}

export default function LiveFromChinaGallery() {
  const { t } = useLang();

  return (
    <section className="py-24 md:py-32 bg-[#F5F5F7]" data-testid="gallery-section">
      <div className="pgb-container">
        <div className="max-w-3xl mb-12">
          <span className="eyebrow">{t.common.gallery.eyebrow}</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
            {t.common.gallery.title}
          </h2>
          <p className="mt-5 text-[#536878] leading-relaxed">
            {t.common.gallery.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 grid-rows-[200px_200px_200px_200px] md:grid-rows-[220px_220px_220px] gap-3 md:gap-4">
          {TILES.map((tile) => (
            <Tile key={tile.id} tile={tile} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
